import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layouts/Layout'; 
import { FaUserCircle, FaCalendarCheck, FaBell, FaInfoCircle } from 'react-icons/fa'; 
import './EmployeeDashboard.css'; 
import { Link } from 'react-router-dom';

const EmployeeDashboard = () => {
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileError, setProfileError] = useState('');
    
    const [notices, setNotices] = useState([]);
    const [noticesLoading, setNoticesLoading] = useState(true);
    const [noticesError, setNoticesError] = useState('');

    const baseUrl = 'https://api.burrayhr.com';

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setProfileError('Authentication token not found. Please log in.');
            setProfileLoading(false);
            setNoticesError('Authentication token not found.'); 
            setNoticesLoading(false);
            return;
        }

        const fetchProfile = async () => {
            try {
                const response = await axios.get(`${baseUrl}/api/profile/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setProfileData(response.data);
            } catch (err) {
                console.error('Error fetching profile:', err);
                setProfileError('Failed to load profile data.');
            } finally {
                setProfileLoading(false);
            }
        };
        
        const fetchNotices = async () => {
            try {
                const response = await axios.get(`${baseUrl}/api/notices/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setNotices(response.data || []); 
            } catch (err) {
                console.error('Error fetching notices:', err);
                setNoticesError('Failed to load recent notices.');
            } finally {
                setNoticesLoading(false);
            }
        };

        fetchProfile();
        fetchNotices();

    }, [baseUrl]);

    if (profileLoading || noticesLoading) {
        return <Layout><div>Loading dashboard components...</div></Layout>;
    }

    if (profileError) {
        return <Layout><div>Error: {profileError}</div></Layout>;
    }

    const firstName = profileData?.employee_data?.full_name || 'Employee';

    const quickInfo = [
        { id: 1, title: 'Vacation Leave Available', value: `${profileData?.employee_data?.vacation_leave_days_available || 0}`, icon: <FaCalendarCheck />, color: '#ffc107' },
        { id: 2, title: 'Special Leave Available', value: `${profileData?.employee_data?.special_leave_days_available || 0}`, icon: <FaCalendarCheck />, color: '#17a2b8' },
        { id: 3, title: 'Sick Leave Available', value: `${profileData?.employee_data?.sick_leave_days_available || 0}`, icon: <FaCalendarCheck />, color: '#28a745' },
        { id: 4, title: 'Martenity Leave Available', value: `${profileData?.employee_data?.maternity_leave_days_available || 'Taken'}`, icon: <FaCalendarCheck />, color: '#28a745' }
    ];

    return (
        <Layout>
            <div className="employee-dashboard-container">
                {/* Welcome Section */}
                <div className="dashboard-welcome-section">
                    <FaUserCircle className="welcome-icon" />
                    <div>
                        <h2>Welcome back, {firstName}!</h2>
                        <p>Here's a quick overview of your dashboard.</p>
                    </div>
                </div>

                {/* Quick Info Cards */}
                <div className="quick-info-cards">
                    {quickInfo.map(card => (
                        <div key={card.id} className="info-card">
                            <div style={{marginLeft: '-25px', marginRight: 'auto'}} className={`info-card-icon ${card.color}`}>{card.icon}</div>
                            <div className="info-card-content">
                                <div style={{marginLeft: '-20px', marginRight: 'auto'}} className="info-card-header">

                                    <p style={{ marginBottom: '0.5rem', fontSize: '0.80rem' }}>{card.title}</p>
                                    
                                    <h4>{card.value}</h4>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Main Dashboard Content Area */}
                <div className="dashboard-main-content">
                    <div className="content-section">
                        <h3><FaInfoCircle style={{ marginRight: '8px', color: '#17a2b8' }} />Recent Notices <span style={{ float: 'right', color: '#17a2b8', textDecoration: 'none', fontSize: '15px' }}><Link to="/employee/notices">View All</Link></span></h3>
                        {noticesError ? (
                            <p className="error-message">Error: {noticesError}</p>
                        ) : notices.length > 0 ? (
                            <ul className="notices-list">
                                {notices.slice(0, 2).map(notice => (
                                    <li style={{ padding: '1rem' }} className='notice-item' key={notice.id || notice.title}> 
                                        <strong>{notice.title}:</strong> {notice.content || 'No details provided.'}
                                        {/* Add date if available: <span className="notice-date">{formatDate(notice.created_at)}</span> */} 
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>No recent notices found.</p>
                        )}
                    </div>
                    <div className="content-section">
                        <h3>Quick Actions</h3>
                        {/* Placeholder for quick action buttons */}
                        <Link to="/employee/apply-leave">
                            <button className="action-button">
                                Apply for Leave
                            </button>
                        </Link>

                        <Link to="/employee/leave-history">
                            <button className="action-button">
                                View Leave History
                            </button>
                        </Link>
                        <Link to="/employee/notices">
                            <button className="action-button">
                                Notices Available
                            </button>
                        </Link>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default EmployeeDashboard;